import { useMemo } from "react";
import flatten from "lodash/flatten";
import { useInfiniteQuery } from "react-query";
import type { GetRecommendationsResponse } from "@/react/api/recommendations/recommendationsApi";
import { recommendationsApi } from "@/react/api/recommendations/recommendationsApi";

export const DEFAULT_PAGE_SIZE = 10;
export const RECOMMENDATIONS_QUERY_KEY = "recommendations";

interface UseRecommendationsInfiniteQueryProps {
  initialPage?: number;
  options?: object;
  perPage?: number;
}

export const useRecommendationsInfiniteQuery = ({
  perPage = DEFAULT_PAGE_SIZE,
  initialPage = 1,
  options = {},
}: UseRecommendationsInfiniteQueryProps = {}) => {
  const fetchPage = ({ pageParam = initialPage }) => {
    let page = 1;

    if (typeof pageParam === "number" && pageParam > 0) {
      page = pageParam;
    }

    const params = {
      page,
      per_page: perPage,
    };

    return recommendationsApi.getRecommendations(params);
  };

  const queryKey = [RECOMMENDATIONS_QUERY_KEY, perPage, initialPage];

  const queryOptions = {
    getNextPageParam: (lastPage: GetRecommendationsResponse) =>
      lastPage.has_next_page ? lastPage.page + 1 : undefined,
    getPreviousPageParam: (firstPage: GetRecommendationsResponse) =>
      firstPage.page > 1 ? firstPage.page - 1 : undefined,
    keepPreviousData: true,
    ...options,
  };

  const result = useInfiniteQuery(queryKey, fetchPage, queryOptions);
  const pages = useMemo(() => result.data?.pages || [], [result.data?.pages]);
  const count = pages[0]?.count || 0;
  const pageSize = pages[0]?.per_page || perPage;
  const recommendations = useMemo(
    () => flatten(pages.map(page => page.records)),
    [pages],
  );

  return {
    ...result,
    queryKey,
    recommendations,
    pageSize,
    pages,
    count,
  };
};
